import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParentShell } from './ParentShell';
import { MarkBadge } from './_common';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';

/**
 * Дневник родителя — два режима:
 *
 *   1. Дефолт (без URL-параметров) — предметы со средним за триместр
 *      + лента уроков за последние 7 дней по всем предметам.
 *
 *   2. Предмет × месяц (`?subject=N&month=YYYY-MM`) — открывается по клику
 *      на ячейку расписания. Показываем шапку с названием предмета,
 *      переключатель месяцев и ленту всех уроков по предмету за месяц
 *      (тема, ДЗ, оценка ученика).
 *
 * Для детсада маршрут редиректится в `/parent` на уровне ParentShell —
 * сюда не попадём.
 */
const URL_SCHEMA = { subject: null, month: '' };

export default function ParentDiaryPage() {
  const { child, activeYear } = useParentShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    const qs = new URLSearchParams({
      student_id: String(child.id),
      year_id:    String(activeYear.id),
    });
    if (params.subject) qs.set('subject_id', String(params.subject));
    if (params.month)   qs.set('month', params.month);
    api.get(`/api/parent/journal?${qs.toString()}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [child?.id, activeYear?.id, params.subject, params.month]);

  if (loading) {
    return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем дневник…</div>;
  }
  if (error) {
    return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  if (data.subjectFilter) {
    return (
      <SubjectMonthView
        data={data}
        onChangeMonth={(month) => setParams({ month })}
        onClose={() => setParams({ subject: null, month: '' })}
      />
    );
  }
  return <DefaultDiaryView data={data} />;
}

/* ─── Дефолтный режим: предметы + последние 7 дней ─────────────────── */

function DefaultDiaryView({ data }) {
  const { period, subjects, lessons, class: cls } = data;
  const hasGrades = !!cls?.hasGrades;

  const byDate = {};
  (lessons || []).forEach((l) => {
    if (!byDate[l.date]) byDate[l.date] = { weekday: l.weekday, items: [] };
    byDate[l.date].items.push(l);
  });

  return (
    <div className="pc-body">
      {hasGrades && period && (
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Предметы · {period.label}</div>
            {period.rangeLabel && (
              <span className="pc-card__sub">{period.rangeLabel}</span>
            )}
            <Link to="/parent/grades" className="pc-card__action">Подробнее по предмету →</Link>
          </div>
          <div className="pc-card__body">
            <div className="pc-diary-subjects">
              {(subjects || []).map((s) => (
                <Link
                  key={s.id}
                  to={`/parent/journal?subject=${s.id}&month=${currentMonth()}`}
                  className="pc-diary-subj"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="pc-diary-subj__name">{s.name}</div>
                  {s.teacher && <div className="pc-diary-subj__teacher">{s.teacher}</div>}
                  <div
                    className="pc-diary-subj__avg"
                    style={{ color: s.avg ? 'var(--pc-fg)' : 'var(--pc-fg-faint)' }}
                  >
                    {s.avg != null ? Number(s.avg).toFixed(1) : '—'}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="pc-card">
        <div className="pc-card__head">
          <div className="pc-card__title">Уроки и домашние задания</div>
          <span className="pc-card__sub">по дате · последние 7 дней</span>
        </div>
        <div className="pc-card__body">
          {Object.keys(byDate).length === 0 && (
            <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
              Пока нет записей в журнале.
            </div>
          )}
          {Object.entries(byDate).map(([date, d]) => (
            <DayBlock key={date} date={date} weekday={d.weekday} items={d.items} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Режим «Предмет × Месяц» ──────────────────────────────────────── */

function SubjectMonthView({ data, onChangeMonth, onClose }) {
  const { subjectFilter, lessons, year } = data;
  const monthIso   = subjectFilter.month;       // YYYY-MM
  const monthLabel = subjectFilter.monthLabel || monthIso;
  const monthTitle = capitalize(monthLabel);    // «Февраль 2026» — для кнопки
  const isCurrent  = monthIso === currentMonth();

  // Границы учебного года в формате YYYY-MM — за них прыгать смысла нет
  // (ни уроков, ни оценок там у этого ученика быть не может).
  const yearMinMonth = (year?.dateStart || '').slice(0, 7) || null;
  const yearMaxMonth = (year?.dateEnd   || '').slice(0, 7) || null;
  const canPrev = !yearMinMonth || monthIso > yearMinMonth;
  const canNext = !yearMaxMonth || monthIso < yearMaxMonth;
  // «Сейчас» возможен, только если сегодняшний месяц попадает в год.
  const today = currentMonth();
  const todayInYear =
    (!yearMinMonth || today >= yearMinMonth) &&
    (!yearMaxMonth || today <= yearMaxMonth);
  const canGoCurrent = todayInYear && !isCurrent;

  const byDate = {};
  (lessons || []).forEach((l) => {
    if (!byDate[l.date]) byDate[l.date] = { weekday: l.weekday, items: [] };
    byDate[l.date].items.push(l);
  });

  function shift(deltaMonths) {
    if (deltaMonths < 0 && !canPrev) return;
    if (deltaMonths > 0 && !canNext) return;
    onChangeMonth(addMonths(monthIso, deltaMonths));
  }
  function goCurrent() {
    if (canGoCurrent) onChangeMonth(today);
  }

  return (
    <div className="pc-body">
      <div className="pc-card">
        <div className="pc-card__head">
          <button type="button" className="pc-card__action" onClick={onClose} style={{ marginLeft: 0, marginRight: 8 }}>
            ← К дневнику
          </button>
          <div className="pc-card__title">
            {subjectFilter.name}
          </div>
          {subjectFilter.teacher && (
            <span className="pc-card__sub">{subjectFilter.teacher}</span>
          )}
          <div className="pc-month-switch" style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn--sm pc-month-switch__nav"
              onClick={() => shift(-1)}
              disabled={!canPrev}
              title={canPrev ? 'Прошлый месяц' : 'Раньше учебного года'}
            >
              <Icon name="chevron-left" size={12} />
            </button>
            <button
              type="button"
              className={'btn btn--sm pc-month-switch__label' + (!canGoCurrent ? ' is-current' : '')}
              onClick={goCurrent}
              title={
                isCurrent
                  ? 'Текущий месяц'
                  : (todayInYear ? 'К текущему месяцу' : 'Сегодняшний месяц вне учебного года')
              }
              disabled={!canGoCurrent}
            >
              {monthTitle}
            </button>
            <button
              type="button"
              className="btn btn--sm pc-month-switch__nav"
              onClick={() => shift(+1)}
              disabled={!canNext}
              title={canNext ? 'Следующий месяц' : 'Позже учебного года'}
            >
              <Icon name="chevron" size={12} />
            </button>
          </div>
        </div>
        <div className="pc-card__body">
          {Object.keys(byDate).length === 0 && (
            <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
              За этот месяц по предмету «{subjectFilter.name}» записей нет.
            </div>
          )}
          {Object.entries(byDate).map(([date, d]) => (
            <DayBlock key={date} date={date} weekday={d.weekday} items={d.items} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Общая разметка одного «дня» ──────────────────────────────────── */

function DayBlock({ date, weekday, items }) {
  return (
    <div className="pc-diary-day">
      <div className="pc-diary-day__head">
        <span className="pc-diary-day__date">{shortDate(date)}</span>
        <span>{weekday}</span>
        <span className="pc-diary-day__rule" />
        <span style={{ fontSize: 11, color: 'var(--pc-fg-faint)' }}>
          {items.length} {pluralLessons(items.length)}
        </span>
      </div>
      {items.map((l, i) => {
        // Название предмета — ссылка в режим «предмет × месяц» (за месяц
        // этого урока). Полезно и в дефолтной ленте «7 дней», и внутри
        // уже открытого предмета (там клик ничего не меняет, кроме URL).
        const month = (l.date || '').slice(0, 7);
        const subjectHref = l.subjectId
          ? `/parent/journal?subject=${l.subjectId}${month ? `&month=${month}` : ''}`
          : null;
        return (
        <div key={i} className="pc-lesson">
          <SubjectChip color={l.subjectColor} name={l.subjectName} />
          <div>
            <div className="pc-lesson__subj-name">
              {subjectHref
                ? <Link to={subjectHref} className="pc-lesson__subj-link">{l.subjectName}</Link>
                : l.subjectName}
            </div>
            {l.topic && <div className="pc-lesson__topic">{l.topic}</div>}
            {l.mark?.note && <div className="pc-lesson__note">{l.mark.note}</div>}
          </div>
          <div className="pc-lesson__hw">
            <div className="pc-lesson__hw-label">Домашнее задание</div>
            {l.homework || <span style={{ color: 'var(--pc-fg-faint)' }}>—</span>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <MarkBadge
              mark={l.mark || (l.attendance
                ? { attendance: l.attendance, lateMinutes: l.lateMinutes }
                : null)}
            />
          </div>
        </div>
        );
      })}
    </div>
  );
}

function SubjectChip({ color, name }) {
  const bg = color && /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : '#e5e7eb';
  const letter = (name || '').trim().charAt(0).toUpperCase();
  return (
    <span
      className="pc-lesson__subj-icon"
      style={{
        background: bg + '33',
        color: bg,
        width: 32, height: 32,
        fontSize: 14, fontWeight: 700,
      }}
    >
      {letter}
    </span>
  );
}

function shortDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}` : iso;
}

function pluralLessons(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'уроков';
  if (m10 === 1) return 'урок';
  if (m10 >= 2 && m10 <= 4) return 'урока';
  return 'уроков';
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function currentMonth() {
  const d = new Date();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}`;
}

function addMonths(yyyymm, delta) {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mm}`;
}
